import { Box, Grid, TextField } from '@mui/material';

import { Field, useFormikContext } from 'formik';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { FormError } from 'app/components/FormError/FormError';
import { LabeledInput } from 'app/components/RichList/items/CompanyItem/components/LabeledInput';
import { translations } from 'locales/i18n';
import { CreateJobOffering, JobOffering } from 'types/AppTypes';

interface Props {
  offer: JobOffering;
  editMode?: boolean;
}

export const OfferDetails = ({ offer, editMode }: Props) => {
  const { t } = useTranslation();
  const formik = useFormikContext<CreateJobOffering>();

  const readOnlyFields = () => {
    return (
      <div className="section">
        <Grid container className="section__row">
          <Grid item xs>
            <LabeledInput label={t(translations.pages.jobOffers.title())}>
              {offer.title}
            </LabeledInput>
          </Grid>
        </Grid>
        <Grid container className="section__row">
          <Grid item xs>
            <LabeledInput displayAlways label={t(translations.pages.jobOffers.startDate())}>
              {offer.startDate}
            </LabeledInput>
          </Grid>
          {offer.createdAt && (
            <Grid item xs>
              <LabeledInput displayAlways label={t(translations.pages.jobOffers.createdAt())}>
                {new Date(offer.createdAt).toLocaleDateString()}
              </LabeledInput>
            </Grid>
          )}
        </Grid>
        <Grid container className="section__row">
          <Grid item xs>
            <LabeledInput
              label={t(translations.common.description())}
              displayAlways
              noEllipsis
            ></LabeledInput>
            <div className="rich-text">
              <Box className="ql-editor">{parse(offer.description)}</Box>
            </div>
          </Grid>
        </Grid>
      </div>
    );
  };

  if (!editMode || formik === undefined) {
    return readOnlyFields();
  }
  const { errors } = formik;

  return (
    <div className="section">
      <div className="section__header">{t(translations.pages.manageJobOffers.createNew())}</div>
      <Grid container spacing={2} className="section__row">
        <Grid item xs>
          <Field
            as={TextField}
            name="title"
            error={!!errors.title}
            helperText={errors.title}
            required
            label={t(translations.pages.manageJobOffers.title())}
          />
        </Grid>
        <Grid item xs>
          <Field
            as={TextField}
            name="startDate"
            error={!!errors.startDate}
            helperText={errors.startDate}
            required
            label={t(translations.pages.jobOffers.startDate())}
          />
        </Grid>
        <Grid item>
          {offer.createdAt && (
            <Grid item xs>
              <LabeledInput displayAlways label={t(translations.pages.jobOffers.createdAt())}>
                {new Date(offer.createdAt || undefined).toLocaleDateString()}
              </LabeledInput>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid container className="section__row">
        <Grid item xs>
          <div className="section__header">{t(translations.common.description())}</div>
          <Field
            name="description"
            error={!!errors.description}
            helperText={errors.description}
            theme="snow"
          >
            {({ field }) => (
              <ReactQuill
                className="manage-job-offer-detail__body__rte"
                value={field.value}
                onChange={field.onChange(field.name)}
                theme="snow"
                modules={{
                  toolbar: [
                    ['bold', 'italic'],
                    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                    ['clean']
                  ],
                  clipboard: {
                    allowed: {
                      tags: [
                        'a',
                        'b',
                        'strong',
                        'u',
                        's',
                        'i',
                        'p',
                        'br',
                        'ul',
                        'ol',
                        'li',
                        'span'
                      ],
                      attributes: ['href', 'rel', 'target', 'class']
                    },
                    keepSelection: true,
                    substituteBlockElements: true,
                    magicPasteLinks: true,
                    matchVisual: false
                  }
                }}
                formats={[
                  'header',
                  'bold',
                  'italic',
                  'align',
                  'size',
                  'font',
                  'color',
                  'underline',
                  'strike',
                  'blockquote',
                  'list',
                  'bullet',
                  'indent',
                  'link',
                  'image'
                ]}
              />
            )}
          </Field>
          {!!errors.description && <FormError message={errors.description} />}
        </Grid>
      </Grid>
    </div>
  );
};

export default OfferDetails;
